import axios from 'axios';
import { EP_BASE_API, EP_AUTH_REFRESH } from '../constants/endPointConstants';
import { clearTokens, getAccessToken, getAccessTokenExpiresIn, getRefreshToken, setTokens } from './AuthSession';

let pendingRefresh = null;

// Bootstrap, proactive renewal and 401 retries must share the same request:
// each successful refresh consumes the previous refresh token on the server.
const rotate = (refreshToken, replacements = 0) => {
    // Let Axios resolve the base URL just as the regular API client does, so
    // same-origin deployments also work when REACT_APP_API_URL is unset.
    return axios.post(EP_AUTH_REFRESH, { refreshToken }, {
        baseURL: EP_BASE_API,
        timeout: 10000,
        headers: { 'Content-Type': 'application/json' },
    })
        .then(({ data }) => {
            // Logout or a newer login may have replaced this session meanwhile.
            if (getRefreshToken() !== refreshToken) return useReplacement(replacements);
            if (!data?.accessToken || !data?.refreshToken) {
                throw new Error('Incomplete session refresh response.');
            }
            setTokens(data.accessToken, data.refreshToken);
            return data;
        })
        .catch((error) => {
            if (getRefreshToken() !== refreshToken) return useReplacement(replacements);
            if (error?.response?.status === 401) {
                clearTokens();
                return null;
            }
            // Offline, timeouts, tunnel errors and server failures do not revoke
            // a session. Let callers retry without deleting the refresh token.
            throw error;
        });
};

const useReplacement = replacements => {
    const current = getRefreshToken();
    if (!current) return null; // Explicit logout won the race.
    const expiresIn = getAccessTokenExpiresIn();
    if (getAccessToken() && (expiresIn == null || expiresIn > 60))
        return { accessToken: getAccessToken(), refreshToken: current, expiresIn: expiresIn ?? 86400 };
    if (replacements >= 2) throw new Error('Session changed during restoration; retry later.');
    return rotate(current, replacements + 1);
};

export const refreshSession = () => {
    if (pendingRefresh) return pendingRefresh;
    if (!getRefreshToken()) return Promise.resolve(null);
    const run = () => {
        const token = getRefreshToken();
        return token ? rotate(token) : Promise.resolve(null);
    };
    // Serialize rotation across same-origin tabs when Web Locks is available.
    // Read the latest persisted token inside the lock, not before waiting.
    const rotation = navigator.locks?.request
        ? navigator.locks.request('pwf-auth-refresh', run)
        : run();
    pendingRefresh = Promise.resolve(rotation).finally(() => { pendingRefresh = null; });
    return pendingRefresh;
};

export const getValidAccessToken = async () => {
    const expiresIn = getAccessTokenExpiresIn();
    if (getAccessToken() && (expiresIn == null || expiresIn > 60)) return getAccessToken();
    const session = await refreshSession();
    return session?.accessToken || getAccessToken() || '';
};
